import supabase from '../supabase/supabase';

const PROOF_BUCKET = 'zevi-payment-proofs-20260925';
const MIGRATION_HINT =
  'Apply database migration 202609250018 to enable live tracking links and order deletion.';

function safeMessage(message) {
  const allowed = [
    'Unauthorized',
    'A review reason is required',
    'A stock-adjustment reason is required',
    'Adjustment would reduce stock below committed inventory',
    'Invalid fulfilment stage',
    'Order not found',
    'Tracking number is too long'
  ];
  return allowed.includes(message) ? message : 'The requested admin operation could not be completed.';
}

function isMissingFunction(error) {
  return (
    error?.code === 'PGRST202' ||
    error?.code === '42883' ||
    /could not find the function/i.test(error?.message || '')
  );
}

export async function getAdminMetrics(start, end) {
  const { data, error } = await supabase.rpc(
    'zevi_admin_dashboard_20260925',
    {
      input_start: start.toISOString(),
      input_end: end.toISOString()
    }
  );
  if (error) {
    throw new Error('Dashboard metrics could not be loaded.');
  }
  return data || {};
}

function orderSelect(includeTracking) {
  return `
    id,
    order_number,
    customer_name,
    customer_email,
    customer_phone,
    grand_total,
    payment_method,
    payment_status,
    order_status,
    fulfilment_stage,
    ${includeTracking ? 'tracking_number, share_token,' : ''}
    created_at,
    updated_at,
    items:zevi_order_items_20260925(id),
    payment:zevi_payments_20260925(
      id,
      status,
      amount_expected,
      submitted_at
    )
  `;
}

function queryOrders(includeTracking) {
  return supabase
    .from('zevi_orders_20260925')
    .select(orderSelect(includeTracking))
    .order('created_at', { ascending: false })
    .limit(100);
}

async function fillMissingOrderPhones(orders) {
  const missingEmails = [...new Set(
    orders
      .filter((order) => !String(order.customer_phone || '').trim())
      .map((order) => String(order.customer_email || '').trim())
      .filter(Boolean)
  )];

  if (!missingEmails.length) return;

  const { data: customers, error } = await supabase
    .from('zevi_customers_20260925')
    .select('email, phone')
    .in('email', missingEmails);

  if (error) {
    console.warn('Customer phone fallback lookup failed:', error);
    return;
  }

  const phoneByEmail = new Map(
    (customers || []).map((customer) => [
      String(customer.email || '').trim().toLowerCase(),
      customer.phone
    ])
  );

  for (const order of orders) {
    if (!String(order.customer_phone || '').trim()) {
      order.customer_phone = phoneByEmail.get(
        String(order.customer_email || '').trim().toLowerCase()
      ) || '';
    }
  }
}

export async function getOrders() {
  try {
    let trackingReady = true;
    let { data, error } = await queryOrders(true);

    if (error) {
      const fallback = await queryOrders(false);
      if (!fallback.error) {
        data = fallback.data;
        error = null;
        trackingReady = false;
      }
    }

    if (error) {
      console.error('Error loading orders:', error);
      throw new Error('Orders could not be loaded.');
    }

    await fillMissingOrderPhones(data || []);

    const ordersWithProofs = await Promise.all(
      (data || []).map(async (order) => {
        order.tracking_ready = trackingReady;
        
        if (!order.payment?.[0]?.id) {
          try {
            const { data: payments, error: payError } = await supabase
              .from('zevi_payments_20260925')
              .select('id, status, amount_expected, submitted_at')
              .eq('order_id', order.id);
            if (!payError && payments?.length > 0) {
              order.payment = payments;
            } else {
              order.payment = [];
            }
          } catch (err) {
            console.warn('Could not load payment for order', order.id);
            order.payment = [];
          }
        }
        
        if (order.payment?.[0]?.id) {
          try {
            const { data: proofs, error: proofError } = await supabase
              .from('zevi_payment_proofs_20260925')
              .select('id, original_filename, upload_status, storage_path, created_at')
              .eq('payment_id', order.payment[0].id);
            if (proofError) {
              console.warn('Could not load proofs for payment', order.payment[0].id);
              order.payment[0].proofs = [];
            } else {
              order.payment[0].proofs = proofs || [];
            }
          } catch (err) {
            console.warn('Could not load proofs for payment', order.payment[0].id);
            order.payment[0].proofs = [];
          }
        }
        return order;
      })
    );

    return ordersWithProofs;
  } catch (err) {
    console.error('getOrders error:', err);
    throw err;
  }
}

export async function getInventory() {
  const { data, error } = await supabase
    .from('zevi_inventory_20260925')
    .select(`
      id,
      quantity_on_hand,
      quantity_reserved,
      quantity_committed,
      low_stock_threshold,
      updated_at,
      product:zevi_products_20260925(id,name,base_sku,track_inventory),
      variant:zevi_product_variants_20260925(id,name,sku)
    `)
    .order('updated_at', { ascending: false });
  if (error) {
    throw new Error('Inventory could not be loaded.');
  }
  return data || [];
}

export async function getPaymentReviews() {
  try {
    const { data, error } = await supabase
      .from('zevi_payments_20260925')
      .select(`
        id,
        method,
        amount_expected,
        status,
        submitted_at,
        reviewed_at,
        rejection_reason,
        order:zevi_orders_20260925(
          id,
          order_number,
          customer_name,
          customer_email,
          customer_phone
        )
      `)
      .in('status', ['proof_submitted', 'under_review', 'new_proof_requested'])
      .order('submitted_at', { ascending: true });

    if (error) {
      console.error('Error loading payment reviews:', error);
      return [];
    }

    const paymentsWithProofs = await Promise.all(
      (data || []).map(async (payment) => {
        if (!payment.order?.id) {
          try {
            const { data: orders, error: orderError } = await supabase
              .from('zevi_orders_20260925')
              .select('id, order_number, customer_name, customer_email, customer_phone')
              .eq('id', payment.order_id)
              .limit(1);
            if (!orderError && orders?.[0]) {
              payment.order = orders[0];
            } else {
              payment.order = null;
            }
          } catch {
            payment.order = null;
          }
        }
        
        try {
          const { data: proofs, error: proofError } = await supabase
            .from('zevi_payment_proofs_20260925')
            .select('id, original_filename, mime_type, created_at, upload_status, storage_path')
            .eq('payment_id', payment.id);
          if (proofError) {
            console.warn('Could not load proofs for payment', payment.id);
            payment.proofs = [];
          } else {
            payment.proofs = proofs || [];
          }
        } catch (err) {
          console.warn('Could not load proofs for payment', payment.id);
          payment.proofs = [];
        }
        return payment;
      })
    );

    return paymentsWithProofs;
  } catch (err) {
    console.error('getPaymentReviews error:', err);
    throw err;
  }
}

export async function updateOrderStatus(orderId, status, note = '') {
  const { error } = await supabase.rpc(
    'zevi_admin_update_order_status_20260925',
    {
      input_order_id: orderId,
      input_status: status,
      input_note: note
    }
  );
  if (error) {
    throw new Error(safeMessage(error.message));
  }
}

export async function updateFulfilmentStage(orderId, stage) {
  const { error } = await supabase.rpc(
    'zevi_admin_update_fulfilment_20260925',
    {
      input_order_id: orderId,
      input_stage: stage
    }
  );
  if (error) {
    throw new Error(safeMessage(error.message));
  }
}

export async function setTrackingNumber(orderId, trackingNumber) {
  const { error } = await supabase.rpc(
    'zevi_admin_set_tracking_number_20260925',
    {
      input_order_id: orderId,
      input_tracking_number: trackingNumber
    }
  );
  if (error) {
    if (isMissingFunction(error)) throw new Error(MIGRATION_HINT);
    throw new Error(safeMessage(error.message));
  }
}

export async function deleteOrder(orderId) {
  const { error } = await supabase.rpc(
    'zevi_admin_delete_order_20260925',
    {
      input_order_id: orderId
    }
  );
  if (error) {
    if (isMissingFunction(error)) throw new Error(MIGRATION_HINT);
    throw new Error(safeMessage(error.message));
  }
}

export async function reviewPayment(paymentId, action, reason = '') {
  const { error } = await supabase.rpc(
    'zevi_admin_review_payment_20260925',
    {
      input_payment_id: paymentId,
      input_action: action,
      input_reason: reason
    }
  );
  if (error) {
    throw new Error(safeMessage(error.message));
  }
}

export async function confirmOrderByOrderId(orderId) {
  const { error } = await supabase.rpc(
    'zevi_admin_confirm_order_by_order_id_20260925',
    { input_order_id: orderId }
  );
  if (error) {
    if (isMissingFunction(error)) {
      throw new Error(
        'Apply database migration 202609250025 to enable direct order confirmation.'
      );
    }
    throw new Error(safeMessage(error.message));
  }
}

export async function confirmCodPaid(orderId) {
  const { error } = await supabase.rpc(
    'zevi_admin_confirm_cod_paid_20260925',
    { input_order_id: orderId }
  );
  if (error) {
    if (isMissingFunction(error)) {
      throw new Error('Apply database migration 202609250032-confirm-cod-payment.sql before confirming COD collection.');
    }
    throw new Error(error.message || 'COD payment could not be confirmed.');
  }
}

export async function adjustInventory(inventoryId, quantity, mode, reason) {
  const { data, error } = await supabase.rpc(
    'zevi_admin_adjust_inventory_20260925',
    {
      input_inventory_id: inventoryId,
      input_quantity: Number(quantity),
      input_mode: mode,
      input_reason: reason
    }
  );
  if (error) {
    throw new Error(safeMessage(error.message));
  }
  return data;
}

export async function getSignedProofUrl(proofId) {
  const { data: path, error: pathError } = await supabase.rpc(
    'zevi_admin_get_payment_proof_path_20260925',
    {
      input_proof_id: proofId
    }
  );

  if (pathError || !path) {
    throw new Error('The payment proof is unavailable.');
  }

  const { data, error } = await supabase.storage
    .from(PROOF_BUCKET)
    .createSignedUrl(path, 300);

  if (error) {
    console.error('Payment proof signed URL failed:', error);
    if (
      /Object not found/i.test(error.message || '')
    ) {
      throw new Error(
        'The proof path was not found in Storage. Verify the uploaded file and its recorded path.'
      );
    }
    if (error.status === 400 || error.statusCode === 400) {
      throw new Error(
        `Storage rejected the proof request: ${error.message || 'Bad Request'}. Check the admin Storage read policy and proof path.`
      );
    }
    throw new Error('A secure proof link could not be created.');
  }

  if (!data?.signedUrl) {
    throw new Error('A secure proof link could not be created.');
  }

  return data.signedUrl;
}

export async function getSignedProofUrlByPath(storagePath) {
  if (!storagePath) {
    throw new Error('The payment proof path is missing.');
  }
  const { data, error } = await supabase.storage
    .from(PROOF_BUCKET)
    .createSignedUrl(storagePath, 300);

  if (error) {
    console.error('Payment proof signed URL failed:', error);
    if (
      /Object not found/i.test(error.message || '')
    ) {
      throw new Error(
        'The proof path was not found in Storage. Verify the uploaded file and its recorded path.'
      );
    }
    if (error.status === 400 || error.statusCode === 400) {
      throw new Error(
        `Storage rejected the proof request: ${error.message || 'Bad Request'}. Check the admin Storage read policy and proof path.`
      );
    }
    throw new Error('A secure proof link could not be created.');
  }

  if (!data?.signedUrl) {
    throw new Error('A secure proof link could not be created.');
  }

  return data.signedUrl;
}